import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Truncate from 'react-truncate';
import { errorMessage } from '../../actions/internal';
import { useConversationsContext } from '../../contexts/ConversationsContext';
import { useConversations, useDebounce } from '../../hooks/useConversations';
import { formatDateTimeDay } from '../../utils/Formatters';
import ConversationFilter from './ConversationFilter';

const ConversationItem = memo(({ conversation, isSelected, onSelect, isLast }) => {
  return (
    <div
      className={classNames('o-communication__conversation-item', {
        'o-communication__conversation-item--empty': conversation.unreadCount === 0,
        'o-communication__conversation-item--selected': isSelected,
        'o-communication__conversation-item--last': isLast,
      })}
      onClick={() => onSelect(conversation.id)}>
      <div className="o-avatar u-margin-right--base o-communication__conversation-item-avatar">
        {conversation.avatarUrl && <img src={conversation.avatarUrl} alt=""/>}
      </div>
      <div className="o-communication__conversation-item-content">
        <div className="o-communication__conversation-item-content--header">
          <div className="o-communication__conversation-item-content--name u-txt--bold">{conversation.name}</div>
          <div className="o-communication__conversation-item-content--date-time u-txt--small-xs">
            {formatDateTimeDay(conversation.lastMessageAt)}
          </div>
        </div>
        <div className="o-communication__conversation-item-content--main">
          <div className="o-communication__conversation-item-content--message">
            <Truncate lines={1} ellipsis="..." trimWhitespace={true} width={300}>
              {conversation.lastMessage}
            </Truncate>
          </div>

          {conversation.unreadCount > 0 && (
            <div className="o-communication__conversation-item-content--badge"><span
              className="badge">{conversation.unreadCount}</span></div>
          )}
        </div>
      </div>
    </div>
  );
});

const ConversationList = ({
  errorMessage,
}) => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    // refetch,
  } = useConversations({ unreadOnly, query: debouncedSearchQuery });

  const listContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isRestoringScrollRef = useRef(false);

  useEffect(() => {
    if (isError) {
      errorMessage('Oops! Something went wrong. Contact support for help.');
      console.error('Error loading conversations:', error);
    }
  }, [isError, error, errorMessage]);

  const observerRef = useRef(null);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const { updateConversationsData } = useConversationsContext();

  // Save the scroll position when fetching the next page
  useEffect(() => {
    if (isFetchingNextPage && listContainerRef.current) {
      scrollPositionRef.current = listContainerRef.current.scrollTop;
    }
  }, [isFetchingNextPage]);

  // Restore the scroll position after fetching the next page
  useEffect(() => {
    if (!isFetchingNextPage && listContainerRef.current && scrollPositionRef.current > 0 && !isRestoringScrollRef.current) {
      isRestoringScrollRef.current = true;

      // Используем setTimeout для выполнения после отрисовки
      setTimeout(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTop = scrollPositionRef.current;
        }
        isRestoringScrollRef.current = false;
      }, 0);
    }
  }, [isFetchingNextPage, data]);

  const loaderRef = useCallback(node => {
    if (isLoading || isFetchingNextPage) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });

    if (node) {
      observerRef.current.observe(node);
    }
  }, [isLoading, isFetchingNextPage, fetchNextPage, hasNextPage]);

  const handleConversationClick = (conversationId) => {
    setSelectedConversationId(conversationId);
    updateConversationsData({ selectedConversationId: conversationId });
  };

  const handleFilterChange = (value) => {
    setUnreadOnly(value);
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
    scrollPositionRef.current = 0;
  };

  // Reset scroll position when debouncedSearchQuery or unreadOnly changes
  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
    scrollPositionRef.current = 0;
  }, [debouncedSearchQuery, unreadOnly]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const allConversations = useMemo(() => {
    if (!data) return [];

    const allItems = data.pages.flatMap(page => page.data || []);

    // Use Map to clean up duplicates based on conversation ID
    const uniqueItemsMap = new Map();
    allItems.forEach(item => {
      uniqueItemsMap.set(item.id, item);
    });

    return Array.from(uniqueItemsMap.values());
  }, [data]);

  const renderConversations = () => {
    if (!isLoading && (!allConversations || allConversations.length === 0)) {
      return <div className="o-communication__conversation-list--loading"><p>No conversations available</p></div>;
    }

    const lastIndex = allConversations.length - 1;

    return allConversations.map((conversation, idx) => (
      <ConversationItem
        key={conversation.id}
        conversation={conversation}
        isSelected={selectedConversationId === conversation.id}
        onSelect={handleConversationClick}
        isLast={idx === lastIndex}/>
    ));
  };

  return (
    <div className="medium-4 o-communication__container--left">
      <ConversationFilter onSearchChange={handleSearch} onFilterChange={handleFilterChange} />
      <div
        ref={listContainerRef}
        className={classNames('o-communication__conversation-list',
          { 'o-communication__conversation-list--loading': isLoading })}>
        {renderConversations()}
        {hasNextPage && (
          <div
            className="o-communication__loader"
            ref={loaderRef}
            style={{ height: '20px', opacity: isFetchingNextPage ? '0.5' : '1' }}>
            {isFetchingNextPage ? 'Loading...' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

ConversationList.propTypes = {
  errorMessage: PropTypes.func.isRequired,
};

const mapStateToProps = _state => ({});

export default connect(mapStateToProps, { errorMessage })(ConversationList);
